/**
 * StopsReferentialMap — carte Leaflet partagée par Arrêts, POI MBC et Inter-arrêts.
 * Source unique : la liste `stops` reçue en props (aucune copie locale de données).
 *
 *  - onMapClick(lat, lng)            : clic gauche sur la carte (création directe)
 *  - onContextMenu(lat, lng, point)  : clic droit (menu « Ajouter un POI ici »)
 *  - onMarkerClick(stop)             : clic marqueur (ouvre la fiche)
 *  - draggable | draggableStopId     : tous les marqueurs ou un seul déplaçable
 *  - onMarkerDragEnd(stop, lat, lng) : fin de déplacement
 *  - revertNonce                     : incrémenter pour remettre le marqueur déplacé à sa position serveur
 *  - focusStopId                     : arrêt à centrer (change → flyTo)
 *  - link: { origin, destination }   : liaison inter-arrêt à mettre en évidence
 *  - extraMarkers: [{ id, latitude, longitude, label, color, onClick }] : candidats (ex. résolution de conflit)
 *  - links: inter-arrêts réels [{ id, origin_stop, destination_stop, distance_meters, travel_time_seconds, active }] → lignes cliquables
 */
import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { styleOf, markerHtml } from "@/lib/stopStyles";
import { formatKm, formatMinutes } from "@/lib/stopsUnits";

const ICON_CACHE = new Map();
function cachedIcon(key, html, size) {
  let ic = ICON_CACHE.get(key);
  if (!ic) {
    ic = L.divIcon({ className: "", html, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
    ICON_CACHE.set(key, ic);
  }
  return ic;
}

const DEFAULT_CENTER = [46.5093, 6.4974]; // Morges

const stopIcon = (s, { selected, inactive, role, moving }) => {
  if (role) {
    const bg = role === "origin" ? "#059669" : "#DC2626";
    return cachedIcon(`role-${role}`, markerHtml({ color: bg, icon: "pin", size: 28, badge: role === "origin" ? "A" : "B" }), 28);
  }
  const st = styleOf(s);
  const size = selected || moving ? 28 : 24;
  const key = `${st.color}|${st.icon}|${selected ? 1 : 0}|${inactive ? 1 : 0}|${moving ? 1 : 0}`;
  return cachedIcon(key, markerHtml({ color: st.color, icon: st.icon, size, selected, inactive, moving, badge: moving ? "✥" : "" }), size);
};

function EventsCapture({ onMapClick, onContextMenu }) {
  useMapEvents({
    click(e) { if (onMapClick) onMapClick(e.latlng.lat, e.latlng.lng); },
    contextmenu(e) {
      if (!onContextMenu) return;
      L.DomEvent.preventDefault(e.originalEvent);
      onContextMenu(e.latlng.lat, e.latlng.lng, { x: e.containerPoint.x, y: e.containerPoint.y });
    },
  });
  return null;
}

function FocusController({ focusStop, link, stops }) {
  const map = useMap();
  useEffect(() => {
    if (link?.origin && link?.destination) {
      map.fitBounds(
        [[link.origin.latitude, link.origin.longitude], [link.destination.latitude, link.destination.longitude]],
        { padding: [60, 60], maxZoom: 15 },
      );
    }
  }, [map, link?.origin?.id, link?.destination?.id, link?.origin?.latitude, link?.origin?.longitude, link?.destination?.latitude, link?.destination?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focusStop) map.flyTo([focusStop.latitude, focusStop.longitude], Math.max(map.getZoom(), 15), { duration: 0.6 });
  }, [map, focusStop?.id, focusStop?.latitude, focusStop?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!focusStop && !link && stops.length) {
      map.fitBounds(stops.map((s) => [s.latitude, s.longitude]), { padding: [40, 40], maxZoom: 14 });
    }
  }, [map, stops.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function StopsReferentialMap({
  stops = [],
  focusStopId,
  link,
  draggable = false,
  draggableStopId = null,
  revertNonce = 0,
  onMapClick,
  onContextMenu,
  onMarkerClick,
  onMarkerDragEnd,
  height = "100%",
  hint,
  extraMarkers = [],
  links = [],
  selectedLinkId = null,
  onLinkClick,
  testId = "stops-map",
  children,
}) {
  const focusStop = useMemo(() => stops.find((s) => s.id === focusStopId) || null, [stops, focusStopId]);
  const roleOf = (s) => (link?.origin?.id === s.id ? "origin" : link?.destination?.id === s.id ? "destination" : null);
  const footer = hint ?? (onMapClick ? "Cliquer sur la carte pour ajouter un arrêt" : onContextMenu ? "Clic droit sur la carte → « Ajouter un POI ici »" : null);

  return (
    <div data-testid={testId} style={{ height }} className="relative border border-zinc-900 bg-zinc-100">
      <MapContainer center={DEFAULT_CENTER} zoom={12} className="h-full w-full" style={{ minHeight: 320 }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <EventsCapture onMapClick={onMapClick} onContextMenu={onContextMenu} />
        <FocusController focusStop={focusStop} link={link} stops={stops} />
        {link?.origin && link?.destination && (
          <Polyline
            positions={[[link.origin.latitude, link.origin.longitude], [link.destination.latitude, link.destination.longitude]]}
            pathOptions={{ color: "#0A0A0B", weight: 3, dashArray: "8 6", opacity: 0.85 }}
          />
        )}
        {links.map((l) => l.origin_stop && l.destination_stop && (
          <Polyline
            key={`link-${l.id}`}
            positions={[[l.origin_stop.latitude, l.origin_stop.longitude], [l.destination_stop.latitude, l.destination_stop.longitude]]}
            pathOptions={{ color: l.id === selectedLinkId ? "#FFC60A" : l.active === false ? "#A1A1AA" : "#0A0A0B", weight: l.id === selectedLinkId ? 5 : 3, opacity: 0.8 }}
            eventHandlers={{ click: () => onLinkClick && onLinkClick(l) }}
          >
            <Popup>
              <div className="text-xs space-y-0.5" data-testid={`link-popup-${l.id}`}>
                <div><b>{l.origin_stop.name}</b> → <b>{l.destination_stop.name}</b></div>
                <div className="font-mono">{formatKm(l.distance_meters)} · {formatMinutes(l.travel_time_seconds)}</div>
                {l.active === false && <div className="text-zinc-500">Inactif</div>}
              </div>
            </Popup>
          </Polyline>
        ))}
        {stops.map((s) => {
          const moving = draggableStopId === s.id;
          return (
            <Marker
              key={moving ? `${s.id}-${revertNonce}` : s.id}
              position={[s.latitude, s.longitude]}
              icon={stopIcon(s, { selected: s.id === focusStopId, inactive: s.active === false, role: roleOf(s), moving })}
              draggable={draggable || moving}
              eventHandlers={{
                click: (e) => { L.DomEvent.stopPropagation(e); onMarkerClick && onMarkerClick(s); },
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  onMarkerDragEnd && onMarkerDragEnd(s, lat, lng);
                },
              }}
            >
              <Tooltip direction="top" offset={[0, -12]}>
                <span className="font-mono text-xs">{s.name}{s.active === false ? " · inactif" : ""}</span>
              </Tooltip>
            </Marker>
          );
        })}
        {extraMarkers.map((m) => (
          <Marker
            key={`extra-${m.id}`}
            position={[m.latitude, m.longitude]}
            zIndexOffset={1000}
            icon={L.divIcon({
              className: "",
              html: `<div data-testid="candidate-marker-${m.id}" style="width:30px;height:30px;background:${m.color || "#7C3AED"};color:#fff;border:2px solid #0A0A0B;border-radius:4px;display:flex;align-items:center;justify-content:center;font-family:'IBM Plex Mono',monospace;font-weight:800;font-size:13px;box-shadow:0 0 0 4px rgba(124,58,237,.3);cursor:pointer;">${m.label || "?"}</div>`,
              iconSize: [30, 30], iconAnchor: [15, 15],
            })}
            eventHandlers={{ click: (e) => { L.DomEvent.stopPropagation(e); m.onClick && m.onClick(m); } }}
          >
            <Tooltip direction="top" offset={[0, -14]}><span className="font-mono text-xs">{m.tooltip || m.label}</span></Tooltip>
          </Marker>
        ))}
      </MapContainer>
      {children}
      {footer && (
        <div className="absolute bottom-2 left-2 z-[400] bg-white/95 border border-zinc-900 px-2 py-1 text-[10px] font-mono uppercase tracking-widest">
          {footer}
        </div>
      )}
    </div>
  );
}
